import { runtimeConfig } from "@/runtime-config";
import { useAppSelector } from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { APP_VERSION, IS_PROD } from "@/utils/env-constants";
import { selectProjectId } from "@faro-lotv/app-component-toolbox";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

// Initialize Sentry when loading this module (i.e., as soon as possible)
Sentry.init({
  dsn: runtimeConfig.backendEndpoints.sentryDsn,
  release: APP_VERSION,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
  ],
  environment: runtimeConfig.deployment.environment,

  tracesSampleRate: IS_PROD ? 0.5 : 1,
});

/**
 * Allowing Sentry to inject itself into the router
 * See https://docs.sentry.io/platforms/javascript/guides/react/features/react-router/v6/#usage-with-routes--component
 */
export const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

/**
 * Initialize the logging system (Sentry for now)
 */
export function useLogging(): void {
  // For logged in users add the user info to sentry context
  const currentUser = useAppSelector(selectCurrentUser);
  useEffect(() => {
    if (!currentUser) return;

    Sentry.setUser({
      id: currentUser.id,
      email: currentUser.email,
    });
  }, [currentUser]);

  // Add project id to the sentry context
  const projectId = useAppSelector(selectProjectId);
  useEffect(() => {
    if (projectId === undefined) return;

    Sentry.setContext("project", {
      id: projectId,
    });
  }, [projectId]);
}

/**
 * Log an Error in the logging system
 *
 * @param error to log
 */
export function logError(error: Error | unknown): void {
  Sentry.captureException(error);
}
